import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useImperativeHandle,
} from 'react'
import {
  chakra,
  Icon,
  VStack,
  useToast,
  Text,
  Flex,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Center,
  Spinner,
  useCheckbox,
  useCheckboxGroup,
  HStack,
  Switch,
  AspectRatio as ChakraAspectRatio,
  Divider,
  TagLeftIcon,
  TagLabel,
  Tag,
  Tooltip,
  Badge,
  IconButton,
} from '@chakra-ui/react'
import { isEmpty, isNull } from 'lodash'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { AiOutlineHistory } from 'react-icons/ai'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { Base64 } from 'js-base64'
import ReactDOM from 'react-dom'
import type { UseComboboxReturnValue } from 'downshift'
import { useCombobox } from 'downshift'
import { useHistory } from 'react-router'
import { IoMdCheckmark, IoMdAdd } from 'react-icons/io'
import Highlighter from 'react-highlight-words'
import styled from 'styled-components'
import { useDebounce } from '../../../hooks/useDebounce'
import { UserContext } from '../../../providers'
import type {
  MediaLibrarySearchCollectionResponse,
  MediaLibrarySearchFolderResponse,
  MediaLibrarySearchFileResponse,
  ClientTagResponse,
  ClientTagRequestResponse,
  SearchQueryJson,
  ListItemProps,
  HighlightedTextProps,
  TagPropsInterface,
  RecentSearches,
} from '../../../interfaces'
import {
  getRecentSearchesDescending,
  insertRecentSearch,
  findChunksAtBeginningOfWords,
} from '../utils'
import {
  DEFAULT_SEARCH_NAME_AUTOCOMPLETE_LIMIT,
  DEFAULT_SEARCH_NAME_AUTOCOMPLETE_SKIP,
} from '../../../constants'
import { colors } from '../../../../ui-kit'
import FilePreview from '../FilePreview'
import { mediaLibraryThumbnailTransformation } from '../../../../utils/mediaLibrary'
import { TagGraphic } from '../../../../graphics/media-library/TagIcon'
import { MagnifyingGlassIcon } from '../../../../graphics/media-library/MagnifyingGlassIcon'
import { CollectionIcon } from '../../../../graphics/media-library/CollectionIcon'
import { FolderIcon } from '../../../../graphics/media-library/FolderIcon'
import { FeaturesEnum } from '../../../../utils/features'
import AWSIcon from '../../../../graphics/AWSIcon'
import GoogleIcon from '../../../../graphics/GoogleIcon'
import { SemanticSearchBar } from './SemanticSearchBar'
import { TbX } from 'react-icons/tb'

const AspectRatio = styled(ChakraAspectRatio)`
  & > img,
  & > video {
    object-fit: contain !important;
  }
`

// define wrapper for the highlightedText component
const HighlightedTextComponentWrapper = styled.div`
  display: flex;
  max-width: 100%;
  .highlighted-text-component {
    display: inline-block;
    overflow: hidden;
    text-wrap: nowrap;
    text-overflow: ellipsis;
  }
`
interface Props {
  page: string
  nameValue: string
  outsideClickRef?: React.RefObject<HTMLDivElement>
  inputRect?: DOMRect
  inputActualRect?: DOMRect
  inputBoxLeftElement: React.ReactElement
  inputBoxRightElement: React.ReactElement | null
  semanticSearch?: string
  setSubmitFlag: React.Dispatch<React.SetStateAction<boolean>>
  setNameValue(value: string): void
  setSearchQueryJson(SearchQueryJson: SearchQueryJson): void
  setSemanticSearch?(searchTerm: string): void
  setSearchFocused(value: boolean): void
}

const SearchBarAutoCompleter: React.FC<Props> = ({
  page,
  nameValue,
  inputRect,
  inputBoxLeftElement,
  inputBoxRightElement,
  setSubmitFlag,
  setNameValue,
  setSearchQueryJson,
  outsideClickRef,
  semanticSearch,
  setSemanticSearch,
  setSearchFocused,
}) => {
  const history = useHistory()
  const { t } = useTranslation()
  const toast = useToast()
  const [search, setSearch] = useState<string>(nameValue || semanticSearch || '')
  const localRef = React.useRef<HTMLInputElement>(null)
  const shouldSetInputBoxEmpty = React.useRef<boolean>(true)
  const [assetList, setAssetList] = useState<ListItemProps[]>([])
  const { user } = useContext(UserContext)

  const [fileList, setFileList] = useState<MediaLibrarySearchFileResponse[]>([])
  const [folderList, setFolderList] = useState<
    MediaLibrarySearchFolderResponse[]
  >([])
  const [collectionList, setCollectionList] = useState<
    MediaLibrarySearchCollectionResponse[]
  >([])
  const [tagsList, setTagsList] = useState<ClientTagResponse[]>([])
  const [selectedAssetType, setSelectedAssetType] = useState<string[]>([])
  const [showTagsInFile, setShowTagsInFile] = useState<boolean>(false)
  const [recentSearches, setRecentSearches] = useState<RecentSearches>(
    getRecentSearchesDescending(page === 'media-collection'),
  )
  const [loading, setLoading] = useState<boolean>(false)

  const debouncedSearchTerm = useDebounce(search, 500)
  const { request } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })
  const stateReducer = useCallback(
    (state, actionAndChanges) => {
      const { type, changes } = actionAndChanges
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: false,
          }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
          if (state.highlightedIndex === -1) {
            if (search.trim().length > 0 && showTagsInFile) {
              setNameValue('')
              setSearchQueryJson({
                boolean: 'and',
                filterGroups: [
                  {
                    boolean: 'or',
                    expressions: [
                      {
                        fieldName: 'name',
                        operator: 'HAS',
                        value: search,
                      },
                      {
                        fieldName: 'tags',
                        operator: 'HAS',
                        value: search,
                      },
                    ],
                  },
                ],
              })
            }
            if (search.trim().length > 0) {
              setSemanticSearch?.('')
              setSubmitFlag(true)
              insertRecentSearch(search, page === 'media-collection')
            }
          }
          return changes
        default:
          return changes // otherwise business as usual.
      }
    },
    [search, assetList],
  )

  const generateUrlByType = (
    assetType: string,
    tagsInAsset: boolean,
  ): string => {
    const params: Record<string, string> = {
      skip: DEFAULT_SEARCH_NAME_AUTOCOMPLETE_SKIP.toString(),
      limit: DEFAULT_SEARCH_NAME_AUTOCOMPLETE_LIMIT.toString(),
      sort: 'DESC_RELEVANCE',
      searchQuery: `(type = ${assetType})${
        debouncedSearchTerm
          ? tagsInAsset
            ? ` AND (name HAS "${debouncedSearchTerm}" OR tags HAS "${debouncedSearchTerm}")`
            : ` AND (name HAS "${debouncedSearchTerm}")`
          : ''
      }`,
    }

    const searchParams = new URLSearchParams(params)

    return `?${searchParams.toString()}`
  }

  const getAssets = useCallback(async () => {
    let filePromise: Promise<MediaLibrarySearchFileResponse[]>
    let folderPromise: Promise<MediaLibrarySearchFolderResponse[]>
    let collectionPromise: Promise<MediaLibrarySearchCollectionResponse[]>
    let tagsPromise: Promise<ClientTagRequestResponse>
    if (debouncedSearchTerm.trim().length) {
      setLoading(true)
      if (selectedAssetType.includes('Files')) {
        filePromise = request.get(
          `/api/v2/files${generateUrlByType('file', showTagsInFile)}`,
        ) as Promise<MediaLibrarySearchFileResponse[]>
      } else {
        filePromise = Promise.resolve([])
      }
      if (selectedAssetType.includes('Folders')) {
        folderPromise = request.get(
          `/api/v2/files${generateUrlByType('folder', false)}`,
        ) as Promise<MediaLibrarySearchFolderResponse[]>
      } else {
        folderPromise = Promise.resolve([])
      }
      if (selectedAssetType.includes('Collections')) {
        collectionPromise = request.get(
          `/api/v2/media-collections${generateUrlByType(
            'media-collection',
            false,
          )}`,
        ) as Promise<MediaLibrarySearchCollectionResponse[]>
      } else {
        collectionPromise = Promise.resolve([])
      }
      if (selectedAssetType.includes('Tags')) {
        const params: Record<string, string> = {
          skip: DEFAULT_SEARCH_NAME_AUTOCOMPLETE_SKIP.toString(),
          limit: DEFAULT_SEARCH_NAME_AUTOCOMPLETE_LIMIT.toString(),
          label: debouncedSearchTerm,
        }
        const searchParams = new URLSearchParams(params)
        tagsPromise = request.get(
          `api/clients/${
            user?.clientNumber
          }/client-tags?${searchParams.toString()}`,
        ) as Promise<ClientTagRequestResponse>
      } else {
        tagsPromise = Promise.resolve({
          tagsData: [] as ClientTagResponse[],
          totalTags: 0,
        } as ClientTagRequestResponse)
      }

      const response = await Promise.all([
        folderPromise,
        filePromise,
        collectionPromise,
        tagsPromise,
      ])

      if (request.error) {
        toast({
          description: t(
            'error_fetching_data',
            'Error in fetching data. Try refreshing the page.',
          ),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      } else if (Array.isArray(response)) {
        setFileList(
          response[1] && Array.isArray(response[1]) ? response[1] : [],
        )
        setFolderList(
          response[0] && Array.isArray(response[0]) ? response[0] : [],
        )
        setCollectionList(
          response[2] && Array.isArray(response[2]) ? response[2] : [],
        )
        setTagsList(
          response[3] && response[3].tagsData && Array.isArray(response[3].tagsData)
            ? response[3].tagsData
            : [],
        )
        setAssetList([
          ...[
            ...(response[0] && Array.isArray(response[0]) ? response[0] : []),
            ...(response[1] && Array.isArray(response[1]) ? response[1] : []),
            ...(response[2] && Array.isArray(response[2]) ? response[2] : []),
            ...(response[3]?.tagsData && Array.isArray(response[3].tagsData)
              ? response[3].tagsData
              : []),
          ].map(asset => ({
            item: asset,
            action: () => {
              if ((asset as MediaLibrarySearchFileResponse).type === 'file') {
                history.push(
                  `/media-library/detail/${
                    (asset as MediaLibrarySearchFileResponse).fileId
                  }`,
                )
              } else if (
                (asset as MediaLibrarySearchFolderResponse).type === 'folder'
              ) {
                history.push(
                  `/media-library/${Base64.encode(
                    (asset as MediaLibrarySearchFolderResponse).folderPath,
                  )}`,
                )
              } else if (
                (asset as MediaLibrarySearchCollectionResponse)
                  .mediaCollectionId
              ) {
                history.push(
                  `/media-collections/${
                    (asset as MediaLibrarySearchCollectionResponse)
                      .mediaCollectionId
                  }`,
                )
              } else if ((asset as ClientTagResponse).label) {
                setNameValue('')
                setSemanticSearch?.('')
                setSearchQueryJson({
                  boolean: 'and',
                  filterGroups: [
                    {
                      boolean: 'and',
                      expressions: [
                        {
                          fieldName: 'tags',
                          operator: 'IS_ONE_OF',
                          value: [(asset as ClientTagResponse).label],
                        },
                      ],
                    },
                  ],
                })
                setSubmitFlag(true)
              }
            },
          })),
          {
            item: `Search for all results for ${debouncedSearchTerm}`,
            action: () => {
              shouldSetInputBoxEmpty.current = false
              setNameValue(search)
              setSemanticSearch?.('')
              setSubmitFlag(true)
              if (debouncedSearchTerm.trim().length > 0) {
                insertRecentSearch(
                  debouncedSearchTerm,
                  page === 'media-collection',
                )
              }
            },
          },
          ...(page === 'media-collection'
            ? []
            : [
                {
                  item: `Search all files containing the tag ${debouncedSearchTerm}`,
                  action: () => {
                    setNameValue('path')
                    setSemanticSearch?.('')
                    setSearchQueryJson({
                      boolean: 'and',
                      filterGroups: [
                        {
                          boolean: 'and',
                          expressions: [
                            {
                              fieldName: 'tags',
                              operator: 'IS_ONE_OF',
                              value: [debouncedSearchTerm],
                            },
                          ],
                        },
                      ],
                    })
                    setSubmitFlag(true)
                  },
                },
                {
                  item: `Search for images related to ${debouncedSearchTerm}`,
                  action: () => {
                    shouldSetInputBoxEmpty.current = false
                    setNameValue('')
                    setSemanticSearch?.(search)
                    setSubmitFlag(true)
                  },
                },
              ]),
        ])
      }
      setLoading(false)
    } else {
      setFileList([])
      setFolderList([])
      setCollectionList([])
      setTagsList([])
      if (!isEmpty(recentSearches)) {
        setAssetList(
          Object.entries(recentSearches).map(searchTerm => ({
            item: searchTerm[0],
            action: () => {
              setNameValue(searchTerm[0])
              setSemanticSearch?.('')
              setSubmitFlag(true)
              if (searchTerm[0].trim().length > 0) {
                insertRecentSearch(searchTerm[0], page === 'media-collection')
              }
            },
          })),
        )
      } else {
        setAssetList([])
      }
    }
  }, [debouncedSearchTerm, selectedAssetType, showTagsInFile, recentSearches])

  useEffect(() => {
    void getAssets()
  }, [debouncedSearchTerm, selectedAssetType, showTagsInFile])

  useEffect(() => {
    if (localRef.current) {
      localRef.current.focus()
    }
  }, [])

  useEffect(() => {
    if (
      user?.pricingPlan.featuresList.includes(FeaturesEnum.ACL_AND_COLLECTIONS)
    ) {
      setSelectedAssetType(
        page === 'media-collection'
          ? ['Files', 'Folders', 'Collections']
          : ['Files', 'Folders', 'Collections', 'Tags'],
      )
    } else {
      setSelectedAssetType(['Files', 'Folders', 'Tags'])
    }
  }, [user])

  const {
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectItem,
  } = useCombobox<ListItemProps>({
    items: assetList || [],
    initialInputValue: semanticSearch || nameValue,
    onInputValueChange: ({ inputValue }) => {
      if (!shouldSetInputBoxEmpty.current) {
        shouldSetInputBoxEmpty.current = true
        return
      }
      if (inputValue) {
        setNameValue(inputValue)
        setSearch(inputValue)
      } else {
        setNameValue('')
        setSemanticSearch?.('')
        setSearch('')
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) {
        return
      }
      selectedItem.action()
    },
    itemToString: () => '',
    stateReducer,
  })

  const onCheckboxChange = (value: string[]) => {
    setSelectedAssetType(value)
  }

  const { getCheckboxProps } = useCheckboxGroup({
    defaultValue: user?.pricingPlan.featuresList.includes(
      FeaturesEnum.ACL_AND_COLLECTIONS,
    )
      ? page === 'media-collection'
        ? ['Folders', 'Files', 'Collections']
        : ['Folders', 'Files', 'Collections', 'Tags']
      : ['Folders', 'Files', 'Tags'],
    onChange: onCheckboxChange,
  })

  return ReactDOM.createPortal(
    <VStack
      spacing="0"
      zIndex={1401}
      position="fixed"
      width="58vw"
      height="auto"
      maxHeight="70vh"
      bgColor={colors.appWhite}
      top={inputRect?.top ? `${inputRect.top}px` : 'initial'}
      left={inputRect?.left ? `${inputRect.left}px` : 'initial'}
      borderRadius="0.25rem"
      borderColor="#e6e7ea"
      padding={0}
      ref={outsideClickRef}
      alignItems="flex-start"
    >
      <InputGroup>
        <InputLeftElement
          children={inputBoxLeftElement}
          paddingLeft="7px"
          position="static"
          width="unset"
          cursor="default"
          borderWidth="1px"
          borderRadius="0.25rem"
          borderRightWidth="0"
          borderRightRadius="0"
          borderBottomRadius={
            assetList.length > 0 || debouncedSearchTerm || loading
              ? '0'
              : '0.25rem'
          }
          borderBottomRightRadius="0"
        />
        <Input
          placeholder={t('start_typing_to_search', 'Start typing to search')}
          height="35px"
          borderLeftRadius="0"
          borderLeftWidth="0"
          borderBottomColor="#e6e7ea"
          focusBorderColor="none"
          _hover={{ borderColor: '#e6e7ea' }}
          position="static"
          paddingLeft="unset"
          borderBottomRadius={
            assetList.length > 0 || debouncedSearchTerm || loading
              ? '0'
              : '0.25rem'
          }
          borderBottomLeftRadius="0"
          {...getInputProps({ ref: localRef })}
        />

        {inputBoxRightElement ? (
          <InputRightElement
            width="unset"
            children={
              <>
                {inputBoxRightElement}
                <Divider orientation="vertical" height="1.25em" />
                <IconButton
                  aria-label="Close"
                  icon={<TbX />}
                  colorScheme="#9DA3AE"
                  fontSize="1.25em"
                  variant="outline"
                  border="none"
                  borderRadius="0"
                  borderColor={colors.appPaleLilac}
                  onClick={() => {
                    if (nameValue && nameValue.length > 0) {
                      setNameValue('')
                      selectItem(null)
                    } else {
                      setSearchFocused(false)
                    }
                  }}
                />
              </>
            }
          />
        ) : null}
      </InputGroup>
      {debouncedSearchTerm.length > 0 && (
        <SemanticSearchBar
          page={page}
          search={debouncedSearchTerm}
          item={
            assetList[
              folderList.length +
                fileList.length +
                collectionList.length +
                tagsList.length +
                2
            ]
          }
          index={
            folderList.length +
            fileList.length +
            collectionList.length +
            tagsList.length +
            2
          }
          getItemProps={getItemProps}
        />
      )}
      <HStack
        border="1px solid #e6e7ea"
        borderTop="none"
        borderBottom="none"
        w="100%"
        pt="12px"
        pb="16px"
        px="16px"
        justifyContent="space-between"
      >
        <Flex gap="8px">
          <CustomCheckbox {...getCheckboxProps({ value: 'Folders' })} />
          <CustomCheckbox {...getCheckboxProps({ value: 'Files' })} />
          {user?.pricingPlan.featuresList.includes(
            FeaturesEnum.ACL_AND_COLLECTIONS,
          ) && (
            <CustomCheckbox {...getCheckboxProps({ value: 'Collections' })} />
          )}
          {page !== 'media-collection' && (
            <CustomCheckbox {...getCheckboxProps({ value: 'Tags' })} />
          )}
        </Flex>
        {selectedAssetType.includes('Files') && (
          <Flex gap="10px">
            <Text color="appBattleshipGrey" fontWeight="normal">
              {t('show_tags_in_file_msg', 'Include files with matching tags')}
            </Text>
            <Switch
              isChecked={showTagsInFile}
              onChange={() => void setShowTagsInFile(!showTagsInFile)}
            />
          </Flex>
        )}
      </HStack>
      <VStack
        width="full"
        height="auto"
        alignItems="start"
        justifyContent="space-between"
        bg="white"
        border="1px solid #e6e7ea"
        borderRightWidth="1px"
        borderTop="none"
        borderBottomRadius="0.25rem"
        pb={2}
      >
        <VStack width="full" height="auto" alignItems="start" p={0} m={0}>
          <Flex flexDirection="column" w="full">
            <List maxH="50vh" overflow="auto" width="full" {...getMenuProps()}>
              {folderList.length > 0 && (
                <Text
                  color="appCoolGrey"
                  fontFamily="Poppins"
                  fontSize="14px"
                  fontStyle="normal"
                  fontWeight="500"
                  lineHeight="150%"
                  px="16px"
                  mt="7px"
                >
                  {t('folders', 'Folders')}
                </Text>
              )}

              {folderList.length > 0 &&
                folderList.map((asset, index) => (
                  <ListItem
                    display="flex"
                    justifyContent="space-between"
                    width="full"
                    key={index}
                    backgroundColor={
                      highlightedIndex === index ? '#F3F8FF' : 'appWhite'
                    }
                    paddingY="0.5rem"
                    paddingX="1rem"
                    {...getItemProps({
                      item: assetList[index],
                      index,
                    })}
                  >
                    <Flex w="80%" gap="8px" alignItems="center">
                      <Icon
                        as={FolderIcon}
                        w="24px"
                        h="24px"
                        color={colors.appBattleshipGrey}
                      />
                      <Flex maxW="75%">
                        <HighlightedTextComponent
                          text={asset.name}
                          searchTerm={debouncedSearchTerm}
                        />
                      </Flex>
                      {highlightedIndex === index && (
                        <Text
                          color="appCoolGrey"
                          textAlign="right"
                          fontFamily="Poppins"
                          fontSize="12px"
                          fontStyle="normal"
                          fontWeight="400"
                          lineHeight="150%" /* 18px */
                        >
                          {`• ${moment(asset.createdAt)
                            .local()
                            .format('DD MMM YYYY')}`}
                        </Text>
                      )}
                    </Flex>
                    {highlightedIndex === index && (
                      <Flex w="20%" justifyContent="end" alignItems="center">
                        <Text
                          color="appCoolGrey"
                          textAlign="right"
                          fontFamily="Poppins"
                          fontSize="12px"
                          fontStyle="normal"
                          fontWeight="400"
                          lineHeight="150%"
                          isTruncated
                        >
                          {`Home${asset.folderPath
                            .split('/')
                            .slice(0, -1)
                            .join('/')}`}
                        </Text>
                      </Flex>
                    )}
                  </ListItem>
                ))}

              {fileList.length > 0 && (
                <Text
                  color="appCoolGrey"
                  fontFamily="Poppins"
                  fontSize="14px"
                  fontStyle="normal"
                  fontWeight="500"
                  lineHeight="150%"
                  px="16px"
                  mt="7px"
                >
                  {t('files', 'Files')}
                </Text>
              )}

              {fileList.length > 0 &&
                fileList.map((asset, index) => {
                  index += folderList.length
                  return (
                    <ListItem
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="full"
                      key={index}
                      backgroundColor={
                        highlightedIndex === index ? '#F3F8FF' : 'appWhite'
                      }
                      paddingY="0.5rem"
                      px="16px"
                      gap="8px"
                      {...getItemProps({
                        item: assetList[index],
                        index,
                      })}
                    >
                      <Flex alignItems="center" gap="8px" w="80%">
                        <AspectRatio w="24px" h="24px">
                          <FilePreview
                            file={asset}
                            folderPath={asset.filePath}
                            transformation={mediaLibraryThumbnailTransformation}
                            disablePadding={true}
                          />
                        </AspectRatio>
                        <Flex maxW="70%" gap="8px">
                          {!showTagsInFile && (
                            <Flex maxW="full">
                              <HighlightedTextComponent
                                text={asset.name}
                                searchTerm={debouncedSearchTerm}
                              />
                            </Flex>
                          )}

                          {showTagsInFile &&
                            (() => {
                              let filteredTags: Array<{
                                tagLabel: string
                                info?: any
                              }> =
                                !isNull(asset.tags) || !isNull(asset.AITags)
                                  ? [
                                      ...(asset.tags
                                        ? asset.tags.map(tag => ({
                                            tagLabel: tag,
                                          }))
                                        : []),
                                      ...(asset.AITags
                                        ? asset.AITags.map(AITag => ({
                                            tagLabel: AITag.name,
                                            info: AITag,
                                          }))
                                        : []),
                                    ].filter(
                                      tag =>
                                        findChunksAtBeginningOfWords({
                                          searchWords: [debouncedSearchTerm],
                                          textToHighlight: tag.tagLabel,
                                        }).length > 0,
                                    )
                                  : []

                              let remainingFilteredTagsLength = 0

                              if (filteredTags.length > 1) {
                                remainingFilteredTagsLength =
                                  filteredTags.length - 1
                                filteredTags = filteredTags.slice(0, 1)
                              }
                              return (
                                <Flex maxW="full" gap="8px">
                                  <Flex
                                    maxW={
                                      filteredTags.length ? '200px' : 'full'
                                    }
                                  >
                                    <HighlightedTextComponent
                                      text={asset.name}
                                      searchTerm={debouncedSearchTerm}
                                    />
                                  </Flex>
                                  <Flex w="full" gap="8px" overflow="hidden">
                                    {filteredTags.map((tag, tagIndex) => (
                                      <TagComponent
                                        key={tagIndex}
                                        tag={tag.tagLabel}
                                        searchTerm={debouncedSearchTerm}
                                        maxWidth={
                                          remainingFilteredTagsLength ||
                                          tag.tagLabel.length > 15
                                            ? '150px'
                                            : 'full'
                                        }
                                        icon={
                                          tag.info?.source ===
                                          'google-auto-tagging'
                                            ? GoogleIcon
                                            : tag.info?.source ===
                                              'aws-auto-tagging'
                                            ? AWSIcon
                                            : TagGraphic
                                        }
                                      />
                                    ))}
                                    {remainingFilteredTagsLength > 0 && (
                                      <Flex maxW="80px">
                                        <Tag
                                          color={colors.appBattleshipGrey}
                                          padding="1px 6px 1px 4px"
                                        >
                                          <TagLeftIcon
                                            boxSize="10px"
                                            as={IoMdAdd}
                                            mr={0}
                                          />
                                          <TagLabel fontSize="12px">
                                            {`${remainingFilteredTagsLength} tags`}
                                          </TagLabel>
                                        </Tag>
                                      </Flex>
                                    )}
                                  </Flex>
                                </Flex>
                              )
                            })()}
                        </Flex>
                        {highlightedIndex === index && (
                          <Text
                            color="appCoolGrey"
                            textAlign="right"
                            fontFamily="Poppins"
                            fontSize="12px"
                            fontStyle="normal"
                            fontWeight="400"
                            lineHeight="150%" /* 18px */
                          >
                            {`• ${moment(asset.createdAt)
                              .local()
                              .format('DD MMM YYYY')}`}
                          </Text>
                        )}
                      </Flex>

                      {highlightedIndex === index && (
                        <Flex w="20%" justifyContent="end" alignItems="center">
                          <Text
                            color="appCoolGrey"
                            textAlign="right"
                            fontFamily="Poppins"
                            fontSize="12px"
                            fontStyle="normal"
                            fontWeight="400"
                            lineHeight="150%"
                            isTruncated
                          >
                            {`Home${asset.filePath
                              .split('/')
                              .slice(0, -1)
                              .join('/')}`}
                          </Text>
                        </Flex>
                      )}
                    </ListItem>
                  )
                })}

              {collectionList.length > 0 && (
                <Text
                  color="appCoolGrey"
                  fontFamily="Poppins"
                  fontSize="14px"
                  fontStyle="normal"
                  fontWeight="500"
                  lineHeight="150%"
                  px="16px"
                  mt="7px"
                >
                  {t('collections', 'Collections')}
                </Text>
              )}

              {collectionList.length > 0 &&
                collectionList.map((asset, index) => {
                  index += folderList.length + fileList.length
                  return (
                    <ListItem
                      display="flex"
                      justifyContent="space-between"
                      width="full"
                      key={index}
                      backgroundColor={
                        highlightedIndex === index ? '#F3F8FF' : 'appWhite'
                      }
                      paddingY="0.5rem"
                      paddingX="1rem"
                      {...getItemProps({
                        item: assetList[index],
                        index,
                      })}
                    >
                      <Flex w="80%" gap="8px">
                        <Icon
                          as={CollectionIcon}
                          h="24px"
                          w="24px"
                          color={colors.appBattleshipGrey}
                        />
                        <Flex maxW="75%">
                          <HighlightedTextComponent
                            text={asset.name}
                            searchTerm={debouncedSearchTerm}
                          />
                        </Flex>
                      </Flex>
                      {highlightedIndex === index && (
                        <Flex w="20%" justifyContent="end" alignItems="center">
                          <Text
                            color="appCoolGrey"
                            textAlign="right"
                            fontFamily="Poppins"
                            fontSize="12px"
                            fontStyle="normal"
                            fontWeight="400"
                            lineHeight="150%"
                          >
                            {`${asset.size} item${asset.size === 1 ? '' : 's'}`}
                          </Text>
                        </Flex>
                      )}
                    </ListItem>
                  )
                })}

              {tagsList.length > 0 && (
                <Text
                  color="appCoolGrey"
                  fontFamily="Poppins"
                  fontSize="14px"
                  fontStyle="normal"
                  fontWeight="500"
                  lineHeight="150%"
                  px="16px"
                  mt="7px"
                >
                  {t('tags', 'Tags')}
                </Text>
              )}

              {tagsList.length > 0 &&
                tagsList.map((tag, index) => {
                  index +=
                    folderList.length + fileList.length + collectionList.length
                  return (
                    <ListItem
                      display="flex"
                      justifyContent="space-between"
                      width="full"
                      key={index}
                      backgroundColor={
                        highlightedIndex === index ? '#F3F8FF' : 'appWhite'
                      }
                      paddingY="0.5rem"
                      paddingX="1rem"
                      {...getItemProps({
                        item: assetList[index],
                        index,
                      })}
                    >
                      <Flex maxW="64%" gap="8px" alignItems="center">
                        <TagComponent
                          tag={tag.label}
                          searchTerm={debouncedSearchTerm}
                        />
                      </Flex>
                    </ListItem>
                  )
                })}
            </List>
          </Flex>
        </VStack>
        {fileList.length === 0 &&
          folderList.length === 0 &&
          collectionList.length === 0 &&
          tagsList.length === 0 &&
          debouncedSearchTerm &&
          loading && (
            <Center paddingY={2} w="full">
              <Spinner
                thickness="2px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="md"
              />
            </Center>
          )}
        {fileList.length === 0 &&
          folderList.length === 0 &&
          collectionList.length === 0 &&
          tagsList.length === 0 &&
          debouncedSearchTerm &&
          !loading && (
            <Flex w="full" paddingX="16px" pb={4} justifyContent="flex-start">
              <Text color="appBattleshipGrey">
                {t('no_results_found', 'No results found')}
              </Text>
            </Flex>
          )}
        {debouncedSearchTerm.trim().length && <Divider />}

        {!debouncedSearchTerm && !isEmpty(recentSearches) && (
          <Flex flexDirection="column" w="full">
            <Text
              color="appCoolGrey"
              fontFamily="Poppins"
              fontSize="14px"
              fontStyle="normal"
              fontWeight="500"
              lineHeight="150%"
              px="16px"
              mt={0}
            >
              {t('recent_searches', 'Recent Searches')}
            </Text>
            <List
              maxH="58vh"
              height="auto"
              width="full"
              overflow="auto"
              {...getMenuProps()}
            >
              {Object.entries(recentSearches).map((searchTerm, index) => (
                <ListItem
                  display="flex"
                  justifyContent="space-between"
                  width="full"
                  key={searchTerm[0]}
                  backgroundColor={
                    highlightedIndex === index ? '#F3F8FF' : 'appWhite'
                  }
                  paddingY="0.5rem"
                  paddingX="1rem"
                  {...getItemProps({
                    item: assetList[index],
                    index,
                  })}
                >
                  <Flex w="70%">
                    <Icon
                      as={AiOutlineHistory}
                      boxSize="1.5rem"
                      color="appCoolGrey"
                      mr={2}
                    />
                    <Text isTruncated w="80%" color="appCoolGrey">
                      {searchTerm[0]}
                    </Text>
                  </Flex>
                </ListItem>
              ))}
            </List>
          </Flex>
        )}
        {debouncedSearchTerm.trim().length && (
          <List height="auto" width="full" overflow="auto" {...getMenuProps()}>
            <ListItem
              display="flex"
              justifyContent="space-between"
              width="full"
              backgroundColor={
                highlightedIndex ===
                folderList.length +
                  fileList.length +
                  collectionList.length +
                  tagsList.length
                  ? '#F3F8FF'
                  : 'appWhite'
              }
              paddingY="0.5rem"
              paddingX="1rem"
              {...getItemProps({
                item: assetList[
                  folderList.length +
                    fileList.length +
                    collectionList.length +
                    tagsList.length
                ],
                index:
                  folderList.length +
                  fileList.length +
                  collectionList.length +
                  tagsList.length,
              })}
            >
              <Flex w="100%" gap="8px">
                <Icon
                  as={MagnifyingGlassIcon}
                  h="24px"
                  w="24px"
                  color={colors.appBattleshipGrey}
                />
                <Text>{t('search_all_copy', 'Search all results for')}</Text>
                <Flex maxW="76%">
                  "
                  <Text
                    as="span"
                    color="#1A202C"
                    fontFamily="Poppins"
                    fontSize="14px"
                    fontStyle="normal"
                    fontWeight="600"
                    lineHeight="150%"
                    isTruncated
                  >
                    {debouncedSearchTerm}
                  </Text>
                  "
                </Flex>
              </Flex>
            </ListItem>
            {page !== 'media-collection' && (
              <ListItem
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                width="full"
                backgroundColor={
                  highlightedIndex ===
                  folderList.length +
                    fileList.length +
                    collectionList.length +
                    tagsList.length +
                    1
                    ? '#F3F8FF'
                    : 'appWhite'
                }
                {...getItemProps({
                  item: assetList[
                    folderList.length +
                      fileList.length +
                      collectionList.length +
                      tagsList.length +
                      1
                  ],
                  index:
                    folderList.length +
                    fileList.length +
                    collectionList.length +
                    tagsList.length +
                    1,
                })}
                paddingY="0.5rem"
                paddingX="1rem"
              >
                <Flex maxW="full" gap="8px">
                  <Icon
                    as={MagnifyingGlassIcon}
                    h="24px"
                    w="24px"
                    color={colors.appBattleshipGrey}
                  />
                  <Text>
                    {t(
                      'tag_search_copy',
                      'Search all files containing the tag',
                    )}
                  </Text>
                  <Flex maxW="65%" gap="8px">
                    <TagComponent tag={debouncedSearchTerm} />
                  </Flex>
                </Flex>
              </ListItem>
            )}
          </List>
        )}
      </VStack>
    </VStack>,
    document.body,
  )
}
const TagComponent = ({
  tag,
  searchTerm,
  maxWidth,
  icon = TagGraphic,
}: TagPropsInterface): JSX.Element => (
  <Flex
    alignItems="center"
    justifyContent="start"
    padding="1px 6px 1px 4px"
    gap="2px"
    borderRadius="4px"
    border="1px solid #E6E7EA"
    background="#FFF"
    maxWidth={maxWidth ?? 'full'}
  >
    <Icon
      as={icon}
      w="16px"
      h="16px"
      p={icon === GoogleIcon ? '1.5px' : '0'}
      mt={icon === AWSIcon ? '5px' : '0'}
      mr={icon === AWSIcon ? '1.5px' : '0'}
      color={colors.appBattleshipGrey}
    />
    {searchTerm ? (
      <Flex overflow="hidden">
        <HighlightedTextComponentWrapper>
          <Highlighter
            className="highlighted-text-component"
            searchWords={[searchTerm]}
            textToHighlight={tag}
            findChunks={findChunksAtBeginningOfWords}
            highlightStyle={{
              backgroundColor: '#CDDCF7',
              fontWeight: '500',
              fontStyle: 'normal',
              fontFamily: 'Poppins',
              lineHeight: '150%',
              fontSize: '12px',
            }}
            unhighlightStyle={{
              color: '#1A202C',
              fontWeight: '400',
              fontStyle: 'normal',
              fontFamily: 'Poppins',
              lineHeight: '150%',
              fontSize: '12px',
            }}
          />
        </HighlightedTextComponentWrapper>
      </Flex>
    ) : (
      <Text
        color="#1A202C"
        fonFamily="Poppins"
        fontSize="12px"
        fontStyle="normal"
        fontWeight="400"
        lineHeight="150%"
        isTruncated
      >
        {tag}
      </Text>
    )}
  </Flex>
)

const CustomCheckbox = (props): JSX.Element => {
  const { state, getInputProps, getLabelProps, htmlProps } = useCheckbox(props)
  return (
    <Tooltip label={`Select to include ${props.value.toLowerCase()}`}>
      <chakra.label
        display="flex"
        flexDirection="row"
        alignItems="center"
        gridColumnGap={2}
        maxW="40"
        bg={state.isChecked ? '#E9F4FF' : '#FFF'}
        border="1px solid"
        borderColor="#E6E7EA"
        borderRadius="16px"
        cursor="pointer"
        justifyContent="center"
        padding="6px 16px 5px 16px"
        gap="4px"
        {...htmlProps}
      >
        <input {...getInputProps()} hidden />
        <Text
          color={state.isChecked ? '#0450D5' : '#4A5568'}
          {...getLabelProps()}
        >
          {props.value}
        </Text>
        {state.isChecked && <IoMdCheckmark color="#0450D5" />}
      </chakra.label>
    </Tooltip>
  )
}
const HighlightedTextComponent = ({
  searchTerm,
  text,
}: HighlightedTextProps): JSX.Element => (
  <HighlightedTextComponentWrapper>
    <Highlighter
      className="highlighted-text-component"
      searchWords={[searchTerm]}
      textToHighlight={text}
      findChunks={findChunksAtBeginningOfWords}
      highlightStyle={{
        backgroundColor: '#CDDCF7',
        fontWeight: '500',
        fontStyle: 'normal',
        fontFamily: 'Poppins',
        lineHeight: '150%',
      }}
    />
  </HighlightedTextComponentWrapper>
)

export default SearchBarAutoCompleter
